import { useId } from "react";
import { styled } from "@pigment-css/react";
import { Header, MetaLabel, RowList, RowMeta, RowTitle } from "@acto/taskpane/field-sales-2/components/primitives/Card";

export interface ProfileEntry {
  id: string;
  title: string;
  meta: string;
}

export const ProfileHeader = styled(Header)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[4],
  padding: `${theme.fieldSalesTwo.spacing[10]} ${theme.fieldSalesTwo.spacing[4]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

export const ProfileMeta = styled(RowMeta)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["400"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
}));

export const ProfileSections = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
}));

const Frame = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
  "& + &": {
    paddingTop: theme.fieldSalesTwo.spacing[24],
  },
}));

const Label = styled(MetaLabel)(({ theme }) => ({
  padding: `0 ${theme.fieldSalesTwo.spacing[4]} ${theme.fieldSalesTwo.spacing[8]}`,
}));

const Tile = styled("li")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  padding: `${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[20]}`,
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius["24"],
  boxSizing: "border-box",
}));

const Title = styled(RowTitle)(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[850],
}));

interface ProfileSectionProps {
  label: string;
  entries: ProfileEntry[];
}

/** Renders nothing when `entries` is empty. */
export function ProfileSection({ label, entries }: ProfileSectionProps) {
  const labelId = useId();

  if (entries.length === 0) return null;
  return (
    <Frame aria-labelledby={labelId}>
      <Label id={labelId}>{label}</Label>
      <RowList role="list">
        {entries.map((entry) => (
          <Tile key={entry.id}>
            <Title>{entry.title}</Title>
            <ProfileMeta>{entry.meta}</ProfileMeta>
          </Tile>
        ))}
      </RowList>
    </Frame>
  );
}
